import { useState, useEffect } from 'react';
import { api, getExpiryStatus } from '../api';

const DAY_NAMES = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'];

function getThisWeekDates() {
  const today = new Date();
  const day = today.getDay();
  const monday = new Date(today);
  monday.setDate(today.getDate() - (day === 0 ? 6 : day - 1));
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d.toISOString().split('T')[0];
  });
}

function formatShortDate(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function WeekDayCell({ date, entry, isToday, dayName, onNavigate }) {
  const dishName = entry?.recipe_name || entry?.custom_dish;
  return (
    <div
      className={`dash-week-day ${isToday ? 'today' : ''}`}
      onClick={() => onNavigate('weekmenu')}
    >
      <div className="dash-week-day-name">{dayName}</div>
      <div className="dash-week-day-date">{formatShortDate(date)}</div>
      <div className="dash-week-dish">
        {dishName || <span className="dash-week-empty">—</span>}
      </div>
    </div>
  );
}

export default function Dashboard({ onNavigate }) {
  const [stats, setStats] = useState(null);
  const [expiredProducts, setExpiredProducts] = useState([]);
  const [expiringProducts, setExpiringProducts] = useState([]);
  const [settings, setSettings] = useState({});
  const [weekEntries, setWeekEntries] = useState({});

  const weekDates = getThisWeekDates();
  const todayStr = new Date().toISOString().split('T')[0];

  // Row 1: Ma–Wo (0-2), Row 2: Do–Za (3-5), Row 3: Zo (6)
  const row1 = weekDates.slice(0, 3);
  const row2 = weekDates.slice(3, 6);
  const row3 = weekDates.slice(6);

  useEffect(() => {
    api.get('/stats').then(setStats).catch(() => {});
    api.get('/settings').then(setSettings).catch(() => {});
    api.get('/products?expired=true').then(setExpiredProducts).catch(() => {});
    api.get('/products?expiring=true').then(setExpiringProducts).catch(() => {});
    const start = weekDates[0];
    const end   = weekDates[6];
    api.get(`/week-menu?start=${start}&end=${end}`).then(entries => {
      const map = {};
      for (const e of entries) map[e.date] = e;
      setWeekEntries(map);
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const warningDays = parseInt(settings.expiry_warning_days || '7');

  return (
    <div className="content">
      {/* ── Stat cards ── */}
      <div className="stats-grid">
        <div className="stat-card ok">
          <div className="stat-label">Totaal producten</div>
          <div className="stat-value">{stats?.total ?? '—'}</div>
        </div>
        <div className="stat-card danger">
          <div className="stat-label">Verlopen</div>
          <div className="stat-value">{stats?.expired ?? '—'}</div>
        </div>
        <div className="stat-card warn">
          <div className="stat-label">Bijna verlopen</div>
          <div className="stat-value">{stats?.expiring ?? '—'}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Lades</div>
          <div className="stat-value">{stats?.byDrawer?.length ?? '—'}</div>
        </div>
      </div>

      {/* ── Week menu — 2 rows: Ma–Wo / Do–Zo ── */}
      <div style={{ marginBottom: 24 }}>
        <div className="section-header" style={{ marginBottom: 10 }}>
          <span>📅 Deze week</span>
          <button className="btn btn-ghost btn-sm" onClick={() => onNavigate('weekmenu')}>
            Bewerken →
          </button>
        </div>

        {/* Row 1: Ma / Di / Wo */}
        <div
          className="dash-week-grid"
          style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: 4 }}
        >
          {row1.map((date, i) => (
            <WeekDayCell
              key={date}
              date={date}
              entry={weekEntries[date]}
              isToday={date === todayStr}
              dayName={DAY_NAMES[i]}
              onNavigate={onNavigate}
            />
          ))}
        </div>

        {/* Row 2: Do / Vr / Za */}
        <div
          className="dash-week-grid"
          style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: 4 }}
        >
          {row2.map((date, i) => (
            <WeekDayCell
              key={date}
              date={date}
              entry={weekEntries[date]}
              isToday={date === todayStr}
              dayName={DAY_NAMES[3 + i]}
              onNavigate={onNavigate}
            />
          ))}
        </div>

        {/* Row 3: Zo — full width */}
        <div
          className="dash-week-grid"
          style={{ gridTemplateColumns: '1fr' }}
        >
          {row3.map((date) => (
            <WeekDayCell
              key={date}
              date={date}
              entry={weekEntries[date]}
              isToday={date === todayStr}
              dayName="Zo"
              onNavigate={onNavigate}
            />
          ))}
        </div>
      </div>

      {/* ── Expired products ── */}
      {expiredProducts.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div className="section-header" style={{ marginBottom: 10 }}>
            <span style={{ color: 'var(--md-error)' }}>⚠ Verlopen ({expiredProducts.length})</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {expiredProducts.map(p => {
              const exp = getExpiryStatus(p.expiry_date, warningDays);
              return (
                <div key={p.id} className="alert-row danger">
                  <span style={{ flex: 1, fontWeight: 500, fontSize: 15 }}>{p.name}</span>
                  <span style={{ fontSize: 12, opacity: 0.7 }}>{p.drawer_name || '—'}</span>
                  <span className="expiry-badge danger">{exp?.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Expiring soon ── */}
      {expiringProducts.length > 0 && (
        <div>
          <div className="section-header" style={{ marginBottom: 10 }}>
            <span style={{ color: 'var(--md-warning)' }}>⏰ Bijna verlopen ({expiringProducts.length})</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {expiringProducts.map(p => {
              const exp = getExpiryStatus(p.expiry_date, warningDays);
              return (
                <div key={p.id} className="alert-row warn">
                  <span style={{ flex: 1, fontWeight: 500, fontSize: 15 }}>{p.name}</span>
                  <span style={{ fontSize: 12, opacity: 0.7 }}>{p.drawer_name || '—'}</span>
                  <span className="expiry-badge warn">{exp?.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {stats?.total === 0 && (
        <div className="empty-state">
          <div className="empty-icon">🧊</div>
          <p>Je diepvries is leeg. Voeg je eerste product toe!</p>
        </div>
      )}
    </div>
  );
}
